/**
 * Airline direct booking deeplinks — only for airlines with affiliate programs.
 * Awin tracking parameters added per airline once affiliate IDs are confirmed.
 *
 * Airlines WITHOUT affiliate programs (Ryanair FR, Wizz W6, Vueling VY) are
 * intentionally excluded — we earn nothing from direct traffic to them.
 */

const AFFILIATE_AIRLINES = {
  KL: {
    name: 'KLM',
    url: (o, d, dep, ret) =>
      `https://www.klm.com/search/en/${o}/${d}/${dep}/${ret}/1/0/0/Economy`,
  },
  AF: {
    name: 'Air France',
    url: (o, d, dep, ret) =>
      `https://www.airfrance.com/en/flights?origin=${o}&destination=${d}&outboundDate=${dep}&returnDate=${ret}&adults=1&tripType=R`,
  },
  LH: {
    name: 'Lufthansa',
    url: (o, d, dep, ret) =>
      `https://www.lufthansa.com/xx/en/flight-search?origin=${o}&destination=${d}&outboundDate=${dep}&returnDate=${ret}&adults=1`,
  },
  BA: {
    name: 'British Airways',
    url: (o, d, dep, ret) =>
      `https://www.britishairways.com/en-gb/booking/flights-and-holidays/flight-results?from=${o}&to=${d}&depart=${dep}&return=${ret}&adult=1&cabin=M&trip=R`,
  },
  EK: {
    name: 'Emirates',
    url: (o, d, dep, ret) =>
      `https://www.emirates.com/english/book/search/?type=RT&origin=${o}&destination=${d}&depDate=${dep}&returnDate=${ret}&adults=1`,
  },
  QR: {
    name: 'Qatar Airways',
    url: (o, d, dep, ret) =>
      `https://www.qatarairways.com/en-nl/booking/flight-selection.html?widget=QR&utype=R&adults=1&from=${o}&to=${d}&departDate=${dep}&returnDate=${ret}&cabinType=E`,
  },
  TK: {
    name: 'Turkish Airlines',
    url: (o, d, dep, ret) =>
      `https://www.turkishairlines.com/en-int/flights/?origin=${o}&destination=${d}&departure-date=${dep}&return-date=${ret}&adult=1&cabin=Economy&direction=round-trip`,
  },
  U2: {
    name: 'easyJet',
    url: (o, d, dep, ret) =>
      `https://www.easyjet.com/en/flight?origins=${o}&destinations=${d}&outboundDate=${dep}&inboundDate=${ret}&adult=1`,
  },
  SQ: {
    name: 'Singapore Airlines',
    url: (o, d, dep, ret) =>
      `https://www.singaporeair.com/en_UK/plan-your-trip/search-flights/?ori=${o}&des=${d}&dep=${dep}&ret=${ret}&adt=1&typ=R`,
  },
  CX: {
    name: 'Cathay Pacific',
    url: (o, d, dep, ret) =>
      `https://www.cathaypacific.com/cx/en_NL/search-results.html?type=R&adult=1&origin=${o}&destination=${d}&outboundDate=${dep}&returnDate=${ret}`,
  },
  IB: {
    name: 'Iberia',
    url: (o, d, dep, ret) =>
      `https://www.iberia.com/flights/?departure=${o}&destination=${d}&departureDate=${dep}&returnDate=${ret}&adults=1&tripType=roundTrip`,
  },
}

/**
 * Returns the direct airline booking URL if the airline has an affiliate program,
 * otherwise returns null (no free traffic to non-affiliate carriers).
 */
export function buildAirlineBookingUrl(airlineCode, origin, destination, departureDate, returnDate) {
  if (!airlineCode || !origin || !destination || !departureDate) return null
  const entry = AFFILIATE_AIRLINES[airlineCode.toUpperCase()]
  if (!entry) return null
  return { name: entry.name, url: entry.url(origin, destination, departureDate, returnDate || '') }
}

// Airlines not indexed by Skyscanner — hide the Skyscanner button for these
const NO_SKYSCANNER = new Set(['CZ', 'MU', 'CA', 'HU', 'ZH', '3U', 'FM'])

/** Returns null if the airline is not on Skyscanner */
export function buildSkyscannerUrl(origin, destination, departureDate, returnDate, airlineCode = null) {
  if (!origin || !destination || !departureDate) return null
  if (airlineCode && NO_SKYSCANNER.has(airlineCode.toUpperCase())) return null
  const dep = departureDate.replace(/-/g, '').slice(2) // YYMMDD
  const ret = returnDate ? returnDate.replace(/-/g, '').slice(2) : null
  const path = ret
    ? `/transport/flights/${origin.toLowerCase()}/${destination.toLowerCase()}/${dep}/${ret}/`
    : `/transport/flights/${origin.toLowerCase()}/${destination.toLowerCase()}/${dep}/`
  return `https://www.skyscanner.com${path}`
}

/** Kayak deeplink (affiliate tracking param to be added) */
export function buildKayakUrl(origin, destination, departureDate, returnDate) {
  if (!origin || !destination || !departureDate) return null
  const base = returnDate
    ? `https://www.kayak.com/flights/${origin}-${destination}/${departureDate}/${returnDate}`
    : `https://www.kayak.com/flights/${origin}-${destination}/${departureDate}`
  return base + '/1adults/economy'
}

/** Momondo deeplink — same parent as Kayak, separate brand, strong in Europe */
export function buildMomondoUrl(origin, destination, departureDate, returnDate) {
  if (!origin || !destination || !departureDate) return null
  const base = returnDate
    ? `https://www.momondo.com/flights/${origin}-${destination}/${departureDate}/${returnDate}`
    : `https://www.momondo.com/flights/${origin}-${destination}/${departureDate}`
  return base + '?adults=1'
}

/** Kiwi.com deeplink — good for complex/multi-stop routes */
export function buildKiwiUrl(origin, destination, departureDate, returnDate) {
  if (!origin || !destination || !departureDate) return null
  const params = new URLSearchParams({ from: origin, to: destination, departure: departureDate, adults: '1' })
  if (returnDate) params.set('return', returnDate)
  return `https://www.kiwi.com/deep?${params}`
}

/** Trip.com deeplink — best coverage for Asian/Chinese carriers */
export function buildTripUrl(origin, destination, departureDate, returnDate) {
  if (!origin || !destination || !departureDate) return null
  const params = new URLSearchParams({
    dcity: origin, acity: destination, ddate: departureDate,
    triptype: returnDate ? 'RT' : 'OW', class: 'economy_class', quantity: '1',
  })
  if (returnDate) params.set('rdate', returnDate)
  return `https://www.trip.com/flights/show?${params}`
}
